import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Factory, CheckCircle, XCircle, Play, TreeStructure, Warning, ListChecks, ArrowDown, FlowArrow } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const STATUS = {
  in_progress: { label: "In Produzione", cls: "bg-[#FEF5EB] text-[#F4A261] border-[#F4A261]/20" },
  completed: { label: "Completato", cls: "bg-[#E6F4F1] text-[#2A9D8F] border-[#2A9D8F]/20" },
  cancelled: { label: "Annullato", cls: "bg-[#FBEDEE] text-[#E63946] border-[#E63946]/20" },
};

export default function Produzione() {
  const [formulas, setFormulas] = useState([]);
  const [batches, setBatches] = useState([]);
  const [starting, setStarting] = useState(false);
  const [formulaId, setFormulaId] = useState("");
  const [emergencyMode, setEmergencyMode] = useState(false);
  const [emergencyOutput, setEmergencyOutput] = useState(700);
  const [multiplier, setMultiplier] = useState(1);
  const [notes, setNotes] = useState("");
  const [cascadeOpen, setCascadeOpen] = useState(false);
  const [cascadeFormulaId, setCascadeFormulaId] = useState("");
  const [cascadeMultiplier, setCascadeMultiplier] = useState(1);
  const [cascadePlan, setCascadePlan] = useState(null);
  const [cascadeLoading, setCascadeLoading] = useState(false);
  const [cascadeExecuting, setCascadeExecuting] = useState(false);
  const [instructionsBatch, setInstructionsBatch] = useState(null); // batch obj
  const [instructionsData, setInstructionsData] = useState(null);
  const [instructionsLoading, setInstructionsLoading] = useState(false);
  const [autoCascading, setAutoCascading] = useState(false);

  const openInstructions = async (batch) => {
    setInstructionsBatch(batch);
    setInstructionsData(null);
    setInstructionsLoading(true);
    try {
      const { data } = await api.get(`/batches/${batch.id}/instructions`);
      setInstructionsData(data);
    } catch (err) {
      toast.error(formatApiError(err));
      setInstructionsBatch(null);
    } finally {
      setInstructionsLoading(false);
    }
  };

  const autoProduceMissing = async () => {
    if (!instructionsData) return;
    // Find first sub_step (semilavorato to produce first)
    const subs = instructionsData.ordered_steps.slice(0, -1);
    if (subs.length === 0) {
      toast.info("Nessun semilavorato da produrre");
      return;
    }
    if (
      !window.confirm(
        `Avviare produzione automatica di ${subs.length} semilavorat${
          subs.length === 1 ? "o" : "i"
        }? Saranno consumate le materie prime e i batch saranno completati automaticamente.`
      )
    )
      return;
    setAutoCascading(true);
    try {
      // For each sub-step, start + complete a batch
      for (const step of subs) {
        const { data: started } = await api.post("/batches", {
          formula_id: step.formula_id,
          multiplier: step.multiplier,
          notes: `[AUTO] Cascata per ${instructionsData.batch_number}`,
        });
        await api.post(`/batches/${started.id}/complete`);
        toast.success(
          `${step.formula_name}: prodotti ${step.output_quantity} ${step.output_unit}`
        );
      }
      toast.success("Tutti i semilavorati pronti! Ora puoi completare il batch principale.");
      // Refresh instructions
      await openInstructions(instructionsBatch);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAutoCascading(false);
    }
  };

  const load = useCallback(async () => {
    const [f, b] = await Promise.all([api.get("/formulas"), api.get("/batches")]);
    setFormulas(f.data);
    setBatches(b.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const selectedFormula = formulas.find((f) => f.id === formulaId);

  const effectiveMultiplier = emergencyMode && selectedFormula
    ? Number(emergencyOutput) / Number(selectedFormula.output_quantity)
    : Number(multiplier);

  const startBatch = async (e) => {
    e.preventDefault();
    if (!formulaId) return toast.error("Seleziona una distinta");
    if (emergencyMode && selectedFormula) {
      const min = selectedFormula.min_batch_quantity;
      if (!min) {
        return toast.error("Questa distinta non consente la modalità emergenza");
      }
      if (Number(emergencyOutput) < min || Number(emergencyOutput) >= selectedFormula.output_quantity) {
        return toast.error(
          `Quantità emergenza deve essere tra ${min} e ${selectedFormula.output_quantity - 1} ${selectedFormula.output_unit}`
        );
      }
    }
    try {
      const { data } = await api.post("/batches", {
        formula_id: formulaId,
        multiplier: effectiveMultiplier,
        notes: emergencyMode ? `[EMERGENZA] ${notes}` : notes,
      });
      toast.success(`Batch ${data.batch_number} avviato`);
      setStarting(false);
      setFormulaId("");
      setMultiplier(1);
      setEmergencyMode(false);
      setEmergencyOutput(700);
      setNotes("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const complete = async (id) => {
    try {
      await api.post(`/batches/${id}/complete`);
      toast.success("Batch completato - output caricato a magazzino");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const cancel = async (id) => {
    if (!window.confirm("Annullare il batch? I componenti verranno ripristinati.")) return;
    try {
      await api.post(`/batches/${id}/cancel`);
      toast.success("Batch annullato");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openCascade = () => {
    setCascadeFormulaId("");
    setCascadeMultiplier(1);
    setCascadePlan(null);
    setCascadeOpen(true);
  };

  const computeCascadePlan = async () => {
    if (!cascadeFormulaId) return toast.error("Seleziona una formula");
    setCascadeLoading(true);
    try {
      const { data } = await api.post("/batches/cascade/plan", {
        formula_id: cascadeFormulaId,
        multiplier: Number(cascadeMultiplier),
      });
      setCascadePlan(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCascadeLoading(false);
    }
  };

  const executeCascade = async () => {
    if (!cascadePlan) return;
    if (cascadePlan.missing_leaves.length > 0)
      return toast.error("Impossibile avviare: scorte MP/IMB insufficienti");
    if (
      !window.confirm(
        `Avviare la cascata? Saranno creati ed eseguiti ${cascadePlan.plan.length} batch in sequenza.`
      )
    )
      return;
    setCascadeExecuting(true);
    try {
      const { data } = await api.post("/batches/cascade/execute", {
        formula_id: cascadeFormulaId,
        multiplier: Number(cascadeMultiplier),
        notes: "Avvio cascata multi-livello",
      });
      toast.success(`Cascata completata: ${data.total_steps} batch creati`);
      setCascadeOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCascadeExecuting(false);
    }
  };

  const cascadeFormulaObj = formulas.find((f) => f.id === cascadeFormulaId);

  return (
    <div className="p-8 lg:p-12 fade-up" data-testid="produzione-page">
      <div className="flex items-start justify-between mb-8 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-2">
            Esecuzione Batch · Scorte atomiche
          </div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight text-[#0A0A0A]">
            Produzione
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={openCascade}
            data-testid="cascade-batch-btn"
            className="bg-transparent border border-[#1D3557] text-[#1D3557] hover:bg-[#F3F4F6] px-5 py-3 text-xs font-display font-semibold uppercase tracking-wider flex items-center gap-2 transition-colors"
          >
            <TreeStructure size={14} weight="bold" /> Piano Multi-livello
          </button>
          <button
            onClick={() => setStarting(true)}
            data-testid="new-batch-btn"
            className="bg-[#2A9D8F] hover:bg-[#21867A] text-white px-5 py-3 text-xs font-display font-semibold uppercase tracking-wider flex items-center gap-2 transition-colors"
          >
            <Play size={14} weight="fill" /> Avvia Batch
          </button>
        </div>
      </div>

      {batches.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] p-12 text-center">
          <Factory size={32} weight="duotone" color="#1D3557" className="mx-auto mb-3" />
          <p className="text-sm text-[#4B5563]">
            Nessun batch eseguito. Avvia il primo per consumare materie prime e produrre semilavorati o prodotti finiti.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E7EB] overflow-x-auto" data-testid="batches-table">
          <table className="w-full">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <tr className="text-left text-[10px] uppercase tracking-wider text-[#4B5563]">
                <th className="px-6 py-3 font-semibold">Batch</th>
                <th className="px-6 py-3 font-semibold">Formula</th>
                <th className="px-6 py-3 font-semibold">Output</th>
                <th className="px-6 py-3 font-semibold text-right">Pianif.</th>
                <th className="px-6 py-3 font-semibold text-right">Effett.</th>
                <th className="px-6 py-3 font-semibold">Stato</th>
                <th className="px-6 py-3 font-semibold text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => {
                const s = STATUS[b.status] || STATUS.in_progress;
                return (
                  <tr
                    key={b.id}
                    className="border-b border-[#E5E7EB] hover:bg-[#F9FAFB]"
                    data-testid={`batch-row-${b.id}`}
                  >
                    <td className="px-6 py-4 font-mono-data text-xs text-[#0A0A0A]">
                      <button
                        onClick={() => openInstructions(b)}
                        className="hover:underline hover:text-[#1D3557] text-left"
                        data-testid={`batch-link-${b.id}`}
                        title="Cosa devo fare?"
                      >
                        {b.batch_number}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#0A0A0A]">{b.formula_name}</td>
                    <td className="px-6 py-4 text-sm text-[#4B5563]">{b.output_product_name}</td>
                    <td className="px-6 py-4 text-right font-mono-data text-sm text-[#0A0A0A]">
                      {b.planned_output} L
                    </td>
                    <td className="px-6 py-4 text-right font-mono-data text-sm text-[#4B5563]">
                      {b.actual_output ?? "—"}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-block px-2 py-1 text-[10px] font-semibold uppercase tracking-wider border ${s.cls}`}>
                        {s.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {b.status === "in_progress" ? (
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openInstructions(b)}
                            data-testid={`instructions-batch-${b.id}`}
                            className="bg-[#1D3557] hover:bg-[#0A0A0A] text-white px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1"
                            title="Cosa devo fare per questo batch"
                          >
                            <ListChecks size={12} weight="bold" /> Istruzioni
                          </button>
                          <button
                            onClick={() => complete(b.id)}
                            data-testid={`complete-batch-${b.id}`}
                            className="bg-[#2A9D8F] hover:bg-[#21867A] text-white px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1"
                          >
                            <CheckCircle size={12} weight="fill" /> Completa
                          </button>
                          <button
                            onClick={() => cancel(b.id)}
                            data-testid={`cancel-batch-${b.id}`}
                            className="text-[#4B5563] hover:text-[#E63946] p-1"
                          >
                            <XCircle size={16} weight="bold" />
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-[#4B5563]">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={starting} onOpenChange={setStarting}>
        <DialogContent className="max-w-lg" data-testid="start-batch-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Avvia Nuovo Batch</DialogTitle>
          </DialogHeader>
          <form onSubmit={startBatch} className="space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">
                Formula *
              </label>
              <Select value={formulaId} onValueChange={setFormulaId}>
                <SelectTrigger data-testid="batch-formula-select" className="rounded-none">
                  <SelectValue placeholder="Seleziona formula" />
                </SelectTrigger>
                <SelectContent>
                  {formulas.map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.name} → {f.output_product_name} ({f.output_quantity} {f.output_unit})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563]">
                  Quantità da produrre
                </label>
                {selectedFormula?.min_batch_quantity && (
                  <label className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-semibold text-[#F4A261] cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="emergency-mode-toggle"
                      checked={emergencyMode}
                      onChange={(e) => setEmergencyMode(e.target.checked)}
                      className="w-4 h-4 accent-[#F4A261]"
                    />
                    ⚠ Modalità emergenza
                  </label>
                )}
              </div>

              {emergencyMode && selectedFormula?.min_batch_quantity ? (
                <>
                  <input
                    type="number"
                    step="1"
                    min={selectedFormula.min_batch_quantity}
                    max={selectedFormula.output_quantity - 1}
                    data-testid="emergency-output-input"
                    className="w-full border border-[#F4A261] bg-[#FEF5EB] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#F4A261] font-mono-data"
                    value={emergencyOutput}
                    onChange={(e) => setEmergencyOutput(e.target.value)}
                  />
                  <div className="mt-2 text-xs text-[#4B5563] flex items-center gap-2">
                    <span>
                      Range consentito:{" "}
                      <span className="font-mono-data font-semibold text-[#F4A261]">
                        {selectedFormula.min_batch_quantity}–
                        {selectedFormula.output_quantity - 1} {selectedFormula.output_unit}
                      </span>
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex gap-2 items-center">
                    <input
                      type="number"
                      step="1"
                      min="1"
                      data-testid="batch-multiplier-input"
                      className="w-32 border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#1D3557] font-mono-data"
                      value={multiplier}
                      onChange={(e) => setMultiplier(e.target.value)}
                    />
                    <span className="text-xs text-[#4B5563]">batch interi</span>
                    {selectedFormula && (
                      <span className="text-xs text-[#4B5563] ml-auto font-mono-data">
                        ={" "}
                        <span className="font-semibold text-[#0A0A0A]">
                          {(selectedFormula.output_quantity * Number(multiplier || 0)).toFixed(2)}{" "}
                          {selectedFormula.output_unit}
                        </span>
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>

            {selectedFormula && (
              <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-4">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] mb-2">
                  Consumo Previsto
                </div>
                <ul className="space-y-1">
                  {selectedFormula.components.map((c, i) => (
                    <li key={i} className="flex justify-between text-xs">
                      <span className="text-[#0A0A0A]">{c.product_name}</span>
                      <span className="font-mono-data text-[#4B5563]">
                        {(c.quantity * effectiveMultiplier).toFixed(3)} {c.unit}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">
                Note
              </label>
              <textarea
                rows={2}
                data-testid="batch-notes-input"
                className="w-full border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#1D3557]"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            <DialogFooter>
              <button
                type="button"
                onClick={() => setStarting(false)}
                className="px-5 py-2.5 border border-[#0A0A0A] text-[#0A0A0A] text-xs font-display font-semibold uppercase tracking-wider hover:bg-[#F3F4F6]"
              >
                Annulla
              </button>
              <button
                type="submit"
                data-testid="submit-batch-btn"
                className="bg-[#2A9D8F] hover:bg-[#21867A] text-white px-5 py-2.5 text-xs font-display font-semibold uppercase tracking-wider"
              >
                Avvia Batch
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={cascadeOpen} onOpenChange={setCascadeOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="cascade-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              Piano di Produzione Multi-livello
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-4">
              <p className="text-xs text-[#4B5563] mb-3">
                Seleziona il prodotto finito: il sistema analizza la distinta base e calcola automaticamente
                quali semilavorati (Parte C, Parte D...) devono essere prodotti prima, in base alle scorte attuali.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">
                    Formula prodotto finito
                  </label>
                  <Select value={cascadeFormulaId} onValueChange={(v) => { setCascadeFormulaId(v); setCascadePlan(null); }}>
                    <SelectTrigger data-testid="cascade-formula-select" className="rounded-none">
                      <SelectValue placeholder="Seleziona formula" />
                    </SelectTrigger>
                    <SelectContent>
                      {formulas.map((f) => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.name} → {f.output_product_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] block mb-1">
                    Moltiplicatore
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    data-testid="cascade-multiplier-input"
                    className="w-full border border-[#E5E7EB] bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#1D3557] font-mono-data"
                    value={cascadeMultiplier}
                    onChange={(e) => { setCascadeMultiplier(e.target.value); setCascadePlan(null); }}
                  />
                </div>
              </div>
              {cascadeFormulaObj && (
                <div className="mt-3 text-xs text-[#4B5563]">
                  Output finale richiesto:{" "}
                  <span className="font-mono-data font-semibold text-[#0A0A0A]">
                    {(cascadeFormulaObj.output_quantity * Number(cascadeMultiplier || 0)).toFixed(2)}{" "}
                    {cascadeFormulaObj.output_unit}
                  </span>
                </div>
              )}
              <button
                onClick={computeCascadePlan}
                disabled={!cascadeFormulaId || cascadeLoading}
                data-testid="compute-plan-btn"
                className="mt-4 bg-[#1D3557] hover:bg-[#2A4B7C] text-white px-4 py-2 text-xs font-display font-semibold uppercase tracking-wider disabled:opacity-50"
              >
                {cascadeLoading ? "Calcolo…" : "Calcola Piano"}
              </button>
            </div>

            {cascadePlan && (
              <>
                {cascadePlan.missing_leaves.length > 0 && (
                  <div className="bg-[#FBEDEE] border border-[#E63946]/30 p-4" data-testid="cascade-missing-panel">
                    <div className="flex items-center gap-2 mb-2">
                      <Warning size={16} weight="bold" color="#E63946" />
                      <div className="text-[10px] uppercase tracking-wider font-semibold text-[#E63946]">
                        Scorte insufficienti per MP / Imballaggi
                      </div>
                    </div>
                    <ul className="space-y-1">
                      {cascadePlan.missing_leaves.map((l) => (
                        <li key={l.product_id} className="text-xs flex justify-between">
                          <span className="text-[#0A0A0A]">{l.product_name}</span>
                          <span className="font-mono-data text-[#E63946]">
                            disponibile {l.available.toFixed(3)} · mancano {l.shortage.toFixed(3)} {l.unit}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-[10px] text-[#4B5563] mt-2">
                      Carica le quantità mancanti a magazzino prima di avviare la cascata.
                    </p>
                  </div>
                )}

                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] mb-2">
                    Sequenza di produzione ({cascadePlan.plan.length} step)
                  </div>
                  <div className="bg-white border border-[#E5E7EB]" data-testid="cascade-plan-list">
                    {cascadePlan.plan.map((step, i) => {
                      const isFinal = i === cascadePlan.plan.length - 1;
                      return (
                        <div
                          key={i}
                          className="flex items-center gap-4 p-4 border-b border-[#E5E7EB] last:border-b-0"
                        >
                          <div
                            className={`w-8 h-8 flex items-center justify-center font-mono-data text-xs font-bold ${
                              isFinal ? "bg-[#2A9D8F] text-white" : "bg-[#1D3557] text-white"
                            }`}
                          >
                            {i + 1}
                          </div>
                          <div className="flex-1">
                            <div className="text-sm font-display font-semibold text-[#0A0A0A]">
                              {step.formula_name}
                              {isFinal && (
                                <span className="ml-2 text-[10px] uppercase tracking-wider font-semibold text-[#2A9D8F]">
                                  · Finale
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-[#4B5563] mt-0.5">
                              Produce <span className="font-mono-data">{step.output_product_name}</span> · batch ×{" "}
                              <span className="font-mono-data font-semibold">{step.multiplier}</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-mono-data text-sm font-semibold text-[#0A0A0A]">
                              {step.planned_output.toFixed(2)} {step.output_unit}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={() => setCascadeOpen(false)}
              className="px-5 py-2.5 border border-[#0A0A0A] text-[#0A0A0A] text-xs font-display font-semibold uppercase tracking-wider hover:bg-[#F3F4F6]"
            >
              Chiudi
            </button>
            <button
              type="button"
              onClick={executeCascade}
              disabled={!cascadePlan || cascadePlan.missing_leaves.length > 0 || cascadeExecuting}
              data-testid="execute-cascade-btn"
              className="bg-[#2A9D8F] hover:bg-[#21867A] text-white px-5 py-2.5 text-xs font-display font-semibold uppercase tracking-wider disabled:opacity-50 flex items-center gap-2"
            >
              <Play size={12} weight="fill" />
              {cascadeExecuting ? "Esecuzione…" : "Avvia Cascata"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Step-by-step instructions dialog */}
      <Dialog
        open={!!instructionsBatch}
        onOpenChange={(v) => {
          if (!v) {
            setInstructionsBatch(null);
            setInstructionsData(null);
          }
        }}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="instructions-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2">
              <ListChecks size={20} weight="duotone" color="#1D3557" />
              Cosa Devo Fare · {instructionsBatch?.batch_number}
            </DialogTitle>
          </DialogHeader>

          {instructionsLoading && (
            <div className="py-12 text-center text-sm text-[#4B5563]">Caricamento…</div>
          )}

          {instructionsData && (
            <div className="space-y-4">
              {/* Summary header */}
              <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-4">
                <div className="text-[10px] uppercase tracking-wider text-[#4B5563] mb-1">
                  Obiettivo
                </div>
                <div className="font-display text-lg font-bold text-[#0A0A0A]">
                  Produrre {instructionsData.main_step.output_quantity}{" "}
                  {instructionsData.main_step.output_unit} di{" "}
                  {instructionsData.main_step.output_product_name}
                </div>
                <div className="text-xs text-[#4B5563] mt-1">
                  Formula: {instructionsData.main_step.formula_name}
                </div>
              </div>

              {/* Cascade alert */}
              {instructionsData.ordered_steps.length > 1 && (
                <div className="bg-[#FFF8E7] border border-[#F4A261]/40 p-4">
                  <div className="flex items-start gap-3">
                    <Warning size={20} weight="fill" color="#F4A261" className="flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="font-display font-semibold text-[#0A0A0A] mb-1">
                        Servono altri semilavorati prima di poter procedere
                      </div>
                      <div className="text-xs text-[#4B5563] mb-3">
                        Per fare questo batch devi prima produrre {instructionsData.ordered_steps.length - 1} semilavorat
                        {instructionsData.ordered_steps.length === 2 ? "o" : "i"}.
                      </div>
                      <button
                        onClick={autoProduceMissing}
                        disabled={autoCascading || instructionsData.has_missing_materials}
                        data-testid="auto-produce-btn"
                        className="bg-[#2A9D8F] hover:bg-[#21867A] disabled:opacity-50 text-white px-4 py-2 text-xs font-display font-semibold uppercase tracking-wider flex items-center gap-2"
                      >
                        <FlowArrow size={14} weight="bold" />
                        {autoCascading ? "Produzione in corso…" : "Produci tutto automaticamente"}
                      </button>
                      {instructionsData.has_missing_materials && (
                        <div className="text-[10px] text-[#E63946] mt-2 font-semibold">
                          ⚠ Mancano materie prime — completa lo stock prima di procedere
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Steps */}
              {instructionsData.ordered_steps.map((step, idx) => {
                const isFinal = step.is_final;
                const allOk = step.components.every((c) => c.status === "ok");
                return (
                  <div key={step.formula_id + idx} data-testid={`step-${step.step_number}`}>
                    {idx > 0 && (
                      <div className="flex justify-center my-2">
                        <ArrowDown size={20} weight="bold" color="#1D3557" />
                      </div>
                    )}
                    <div
                      className={`border-2 ${
                        isFinal ? "border-[#1D3557]" : "border-[#F4A261]"
                      } bg-white`}
                    >
                      <div
                        className={`px-5 py-3 flex items-center justify-between ${
                          isFinal ? "bg-[#1D3557] text-white" : "bg-[#F4A261]/10"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span
                            className={`inline-flex items-center justify-center w-7 h-7 font-mono-data font-bold text-sm ${
                              isFinal
                                ? "bg-white text-[#1D3557]"
                                : "bg-[#F4A261] text-white"
                            }`}
                          >
                            {step.step_number}
                          </span>
                          <div>
                            <div
                              className={`text-[10px] uppercase tracking-wider font-semibold ${
                                isFinal ? "text-white/80" : "text-[#F4A261]"
                              }`}
                            >
                              {isFinal ? "Step finale" : "Step preliminare"}
                            </div>
                            <div
                              className={`font-display font-bold ${
                                isFinal ? "text-white" : "text-[#0A0A0A]"
                              }`}
                            >
                              {step.formula_name}
                            </div>
                          </div>
                        </div>
                        <div
                          className={`text-right text-xs font-mono-data ${
                            isFinal ? "text-white" : "text-[#0A0A0A]"
                          }`}
                        >
                          <div className="font-bold">
                            {step.output_quantity} {step.output_unit}
                          </div>
                          <div className={`${isFinal ? "text-white/80" : "text-[#4B5563]"}`}>
                            {step.output_product_name}
                          </div>
                        </div>
                      </div>

                      {step.reason && (
                        <div className="px-5 py-2 bg-[#FFF8E7] border-b border-[#F4A261]/40 text-xs text-[#4B5563]">
                          📌 {step.reason}
                        </div>
                      )}

                      {/* Components checklist */}
                      <div className="p-5">
                        <div className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563] mb-3">
                          Componenti da preparare
                        </div>
                        <div className="space-y-2">
                          {step.components.map((c) => (
                            <div
                              key={c.product_id}
                              className={`flex items-center gap-3 p-2 border ${
                                c.status === "ok"
                                  ? "border-[#2A9D8F]/30 bg-[#E6F4F1]"
                                  : c.status === "short"
                                  ? "border-[#F4A261]/40 bg-[#FFF8E7]"
                                  : "border-[#E63946]/40 bg-[#FBEDEE]"
                              }`}
                            >
                              <span
                                className={`inline-block px-2 py-0.5 text-[10px] font-mono-data font-semibold text-white`}
                                style={{
                                  background:
                                    c.product_type === "materia_prima"
                                      ? "#457B9D"
                                      : c.product_type === "semilavorato"
                                      ? "#1D3557"
                                      : "#F4A261",
                                }}
                              >
                                {c.type_label}
                              </span>
                              <div className="flex-1">
                                <div className="text-sm font-medium text-[#0A0A0A]">
                                  {c.product_name}
                                </div>
                                <div className="text-[10px] font-mono-data text-[#4B5563]">
                                  {c.product_barcode}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="font-mono-data text-sm font-bold text-[#0A0A0A]">
                                  {Number(c.needed).toFixed(3)} {c.unit}
                                </div>
                                <div className="text-[10px] font-mono-data text-[#4B5563]">
                                  Stock: {Number(c.stock).toFixed(2)} {c.unit}
                                </div>
                              </div>
                              {c.status === "ok" && (
                                <CheckCircle size={20} weight="fill" color="#2A9D8F" />
                              )}
                              {c.status === "short" && (
                                <Warning size={20} weight="fill" color="#F4A261" />
                              )}
                              {c.status === "missing" && (
                                <XCircle size={20} weight="fill" color="#E63946" />
                              )}
                            </div>
                          ))}
                        </div>

                        {/* Volume final / water */}
                        {step.water_to_add_L !== null && step.water_to_add_L > 0 && (
                          <div className="mt-3 bg-[#E8F4FD] border border-[#457B9D]/40 p-3 flex items-center justify-between">
                            <div>
                              <div className="text-[10px] uppercase tracking-wider text-[#457B9D] font-semibold">
                                Aggiungere acqua/diluente
                              </div>
                              <div className="text-xs text-[#4B5563]">
                                Volume componenti: {step.components_volume_L} L · Volume finale:{" "}
                                {step.final_volume || step.output_quantity} L
                              </div>
                            </div>
                            <div className="font-mono-data text-lg font-bold text-[#457B9D]">
                              + {step.water_to_add_L.toFixed(2)} L
                            </div>
                          </div>
                        )}

                        {/* Preparation instructions */}
                        {step.preparation_instructions && (
                          <div className="mt-3 bg-[#F9FAFB] border-l-4 border-[#1D3557] p-3">
                            <div className="text-[10px] uppercase tracking-wider text-[#4B5563] font-semibold mb-1">
                              Istruzioni di preparazione
                            </div>
                            <div className="text-sm text-[#0A0A0A] whitespace-pre-wrap">
                              {step.preparation_instructions}
                            </div>
                          </div>
                        )}
                        {step.packaging_instructions && (
                          <div className="mt-3 bg-[#F9FAFB] border-l-4 border-[#F4A261] p-3">
                            <div className="text-[10px] uppercase tracking-wider text-[#4B5563] font-semibold mb-1">
                              Istruzioni di confezionamento
                            </div>
                            <div className="text-sm text-[#0A0A0A] whitespace-pre-wrap">
                              {step.packaging_instructions}
                            </div>
                          </div>
                        )}

                        {isFinal && allOk && (
                          <div className="mt-4 flex justify-end">
                            <button
                              onClick={() => {
                                complete(instructionsBatch.id);
                                setInstructionsBatch(null);
                              }}
                              data-testid="complete-from-instructions-btn"
                              className="bg-[#2A9D8F] hover:bg-[#21867A] text-white px-5 py-2 text-xs font-display font-semibold uppercase tracking-wider flex items-center gap-2"
                            >
                              <CheckCircle size={14} weight="fill" />
                              Completa Batch
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <DialogFooter>
            <button
              type="button"
              onClick={() => {
                setInstructionsBatch(null);
                setInstructionsData(null);
              }}
              className="px-5 py-2.5 border border-[#0A0A0A] text-[#0A0A0A] text-xs font-display font-semibold uppercase tracking-wider hover:bg-[#F3F4F6]"
            >
              Chiudi
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
